import { Parser, Ty } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import { Vector } from 'apache-arrow';
import _ from 'lodash';

export const rowMapper = (
  attributes: Record<string, Ty.ExtendedAttributeType>
) => {
  const mappers = _.mapValues(attributes, (ty) => processVal(ty));

  return (row: Record<string, unknown>) =>
    _.mapValues(mappers, (processor, name) => {
      const res = processor(row[name] ?? null);
      return Parser.LiteralSchema.parse(res);
    });
};

export const processVal =
  (ty: Ty.ExtendedAttributeType) =>
  (val: unknown): unknown => {
    if (val === null) {
      return val;
    }

    if (ty.ty.k === 'primitive') {
      if (typeof val === 'bigint') {
        return Number(val);
      }

      if (ty.ty.t === 'super') {
        Assert.assert(typeof val === 'string');
        return JSON.parse(val);
      }

      if (ty.ty.t === 'timestamp') {
        return new Date(val as number);
      }

      return val;
    } else if (ty.ty.k === 'enum' || ty.ty.k === 'range') {
      return typeof val === 'bigint' ? Number(val) : val;
    } else if (ty.ty.k === 'array') {
      const mapper = processVal(ty.ty.t);
      Assert.assert(val instanceof Vector, `Expected vector, got ${val}`);
      const arrVal = val.toArray();
      return arrVal.map(mapper);
    } else if (ty.ty.k === 'struct') {
      Assert.assert(
        typeof val === 'object' || val === '{}',
        invalidObjectReprErrMessage
      );
      Assert.assert(!_.isEqual(val, {}), impossibleEmptyStructErrMessage);

      // Empty structs are encoded as JSON strings. (grep for COT-4879 to see encoding.)
      const parsed = typeof val === 'string' ? JSON.parse(val) : val;
      Assert.assert(typeof parsed === 'object', `Expected object, got ${val}`);

      return rowMapper(ty.ty.fields)(val as Record<string, unknown>);
    } else if (ty.ty.k === 'record') {
      const parsed = typeof val === 'string' ? JSON.parse(val) : val;
      Assert.assert(typeof parsed === 'object', `Expected object, got ${val}`);
      const inner = processVal(Ty.ty(ty.ty.t));
      return _.mapValues(parsed, inner);
    } else {
      return Assert.unreachable(ty.ty);
    }
  };

const invalidObjectReprErrMessage = `Only empty-structs may be represented as json strings. Else, they are objects.`;

// If this limitation no longer exists, remove the workaround implemented by the PR for COT-4879
const impossibleEmptyStructErrMessage =
  'Impossible. DuckDb has no way to type a value as an empty struct so a value like this could never be generated.';
